import { useEffect, useState } from 'react';

import { UploadedReceipt, LexOfficeBookingData, DatevBookingData } from '../../../types';

interface UseBookingCardStateProps {
  receipt: UploadedReceipt;
}

export interface BookingCardState {
  /**
   * Indicates which booking system this receipt uses.
   * - 'lexoffice' when lexOfficeBooking is present
   * - 'datev' when datevBooking entries are present
   * - 'none' when no booking data is available
   */
  bookingSystem: 'lexoffice' | 'datev' | 'none';
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
  isEditing: boolean;
  setIsEditing: (editing: boolean) => void;
  isImportScheduling: boolean;
  setIsImportScheduling: (importing: boolean) => void;
  isRejecting: boolean;
  setIsRejecting: (rejecting: boolean) => void;
  showImportButton: boolean;
  setShowImportButton: (show: boolean) => void;
  showRejectButton: boolean;
  setShowRejectButton: (show: boolean) => void;
  showValidation: boolean;
  setShowValidation: (show: boolean) => void;
  updatedBookingData: LexOfficeBookingData | undefined;
  setUpdatedBookingData: (data: LexOfficeBookingData | undefined) => void;
  originalBookingData: LexOfficeBookingData | undefined;
  hasChanges: boolean;
  hasMissingCategories: boolean;
  isImported: boolean;
  isRejected: boolean;
  hasGeneratingStatus: boolean;
  hasImportingStatus: boolean;
  // DATEV-specific state
  updatedDatevBookingData: DatevBookingData[] | undefined;
  setUpdatedDatevBookingData: (data: DatevBookingData[] | undefined) => void;
  originalDatevBookingData: DatevBookingData[] | undefined;
  hasDatevChanges: boolean;
}

export const useBookingCardState = ({
  receipt,
}: UseBookingCardStateProps): BookingCardState => {
  // Default expanded for 'generated' or 'import-failed', collapsed for 'imported'/'modified'
  const defaultExpanded = receipt.status === 'generated' || receipt.status === 'import-failed';

  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isEditing, setIsEditing] = useState(false);
  const [isImportScheduling, setIsImportScheduling] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [showImportButton, setShowImportButton] = useState(true);
  const [showRejectButton, setShowRejectButton] = useState(true);
  const [showValidation, setShowValidation] = useState(false);

  const hasLexOfficeBookingData = !!receipt.lexOfficeBooking;
  const hasDatevBookingData =
    Array.isArray(receipt.datevBooking) && receipt.datevBooking.length > 0;

  const bookingSystem: 'lexoffice' | 'datev' | 'none' = hasLexOfficeBookingData
    ? 'lexoffice'
    : hasDatevBookingData
      ? 'datev'
      : 'none';

  const [updatedBookingData, setUpdatedBookingData] = useState<
    LexOfficeBookingData | undefined
  >(receipt.lexOfficeBooking || undefined);

  const originalBookingData = receipt.lexOfficeBooking || undefined;

  // DATEV booking data state
  const [updatedDatevBookingData, setUpdatedDatevBookingData] = useState<
    DatevBookingData[] | undefined
  >(receipt.datevBooking || undefined);

  const originalDatevBookingData = receipt.datevBooking || undefined;

  // Check if ready to import (all line items have categoryId)
  const missingCategoryItems =
    updatedBookingData?.voucherItems.filter(item => !item.categoryId) || [];
  const hasMissingCategories = missingCategoryItems.length > 0;

  // Check if booking data has been modified from original
  const hasChanges =
    JSON.stringify(updatedBookingData) !== JSON.stringify(originalBookingData);

  // Check if DATEV booking data has been modified from original
  const hasDatevChanges =
    JSON.stringify(updatedDatevBookingData) !==
    JSON.stringify(originalDatevBookingData);

  const isImported = receipt.status === 'imported';
  const isRejected = false; // No explicit 'rejected' status in new API - receipts are deleted on reject
  const hasGeneratingStatus = receipt.status === 'generating' || receipt.status === 'generation-scheduled';
  const hasImportingStatus = receipt.status === 'importing' || receipt.status === 'import-scheduled';

  // Auto-reset isImportScheduling when receipt status changes to a non-generated state
  // (e.g. polling picks up 'import-scheduled', 'importing', 'imported', or 'import-failed')
  useEffect(() => {
    if (isImportScheduling && receipt.status !== 'generated' && receipt.status !== 'modified' && receipt.status !== 'import-failed') {
      setIsImportScheduling(false);
    }
  }, [receipt.status, isImportScheduling]);

  // Auto-reset isRejecting when receipt status changes away from generated/modified
  // (cache update from rejectMutation.onSuccess sets the new status synchronously)
  useEffect(() => {
    if (isRejecting && receipt.status !== 'generated' && receipt.status !== 'modified') {
      setIsRejecting(false);
    }
  }, [receipt.status, isRejecting]);

  return {
    bookingSystem,
    isExpanded,
    setIsExpanded,
    isEditing,
    setIsEditing,
    isImportScheduling,
    setIsImportScheduling,
    isRejecting,
    setIsRejecting,
    showImportButton,
    setShowImportButton,
    showRejectButton,
    setShowRejectButton,
    showValidation,
    setShowValidation,
    updatedBookingData,
    setUpdatedBookingData,
    originalBookingData,
    hasChanges,
    hasMissingCategories,
    isImported,
    isRejected,
    hasGeneratingStatus,
    hasImportingStatus,
    // DATEV-specific state
    updatedDatevBookingData,
    setUpdatedDatevBookingData,
    originalDatevBookingData,
    hasDatevChanges,
  };
};
